import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import {
  Message,
  SystemAudio,
  SystemBadgeAudio,
  SystemMessage,
  SystemScrollToMessage,
  SystemShowLessonModal,
  SystemStartRecording,
} from "../types";
import { createPlaceholderMessage } from "../helpers/messageHelpers";
import {
  countTabsWithBaseId,
  ensureUniqueTabIds,
  normalizeTabLabel,
  tabsShareBaseId,
} from "../components/chat/chat-bubble/tabbed-message/normalize";
import { useAppStore } from "../stores/appStore";
import useChatStore from "../stores/chatStore";
import useUserStore from "../stores/userStore";
import { setDomainFocus, checkSessionStatus } from "../axios/user";
import { isMobile } from "react-device-detect";
import { paginateMessages } from "./paginateMessages";
import {
  markSocketConnected,
  runMissedMessageSync,
  scheduleMissedMessageSyncAfterReconnect,
  scheduleMissedMessageSyncAfterTabVisible,
  shouldSyncMissedMessages,
} from "./missedMessageSync";
import { PersonaMenuItem } from "@/types/realtimeSpeech";
import { useAssessmentStore } from "../stores/assessmentStore";
import { AssessmentData, normalizeAssessmentList } from "../axios/quiz";
import { normalizeAssessmentQuestion } from "./assessmentInteractive";
import {
  resolveAssessmentAnswerEditingEnabled,
  resolveAssessmentGamificationEnabled,
  resolveAssessmentShowFeedback,
} from "../components/quiz-pop-up/assessment-contest-utils";
import { isAdminRoute } from "./isAdminRoute";
import { enqueuePendingMessage, flushPendingMessages } from "./messageOutbox";
import useStatisticsStore from "../stores/statisticsStore";
import { syncAuthCookie } from "./authToken";
import { isSessionActivationInFlight } from "./sessionActivation";
import { shouldVerifySocket, sessionRecoveryAction } from "./socketRecoveryPolicy";

type SetMessages = React.Dispatch<React.SetStateAction<Message[]>>;
type SystemEventType =
  | Message
  | SystemMessage
  | SystemAudio
  | SystemBadgeAudio
  | SystemShowLessonModal
  | SystemScrollToMessage
  | SystemStartRecording;

let socketInstance: Socket | null = null;
let listenersAttached = false;
let serverDisconnectVerificationInFlight = false;
let pendingSocketReconnectTimeouts: ReturnType<typeof setTimeout>[] = [];

/**
 * Keep socket.auth and the shared `token` cookie on the same JWT before any
 * handshake. Backend logs cookie on WS upgrade and registers sid from auth —
 * a mismatch yields 401 "user_id for another sid".
 */
const applySocketAuth = (
  socket: Socket,
  token: string,
  focus: string,
  domain: string,
) => {
  syncAuthCookie(token);
  socket.auth = { token, focus, domain };
};

const scheduleSocketReconnect = (
  callback: () => void,
  delayMs: number,
): ReturnType<typeof setTimeout> => {
  const timeoutId = setTimeout(() => {
    pendingSocketReconnectTimeouts = pendingSocketReconnectTimeouts.filter(
      (id) => id !== timeoutId,
    );
    callback();
  }, delayMs);
  pendingSocketReconnectTimeouts.push(timeoutId);
  return timeoutId;
};

export const cancelPendingSocketReconnects = () => {
  pendingSocketReconnectTimeouts.forEach((id) => clearTimeout(id));
  pendingSocketReconnectTimeouts = [];
};

export const isServerDisconnectVerificationInFlight = (): boolean =>
  serverDisconnectVerificationInFlight;

const APP_PING_TEXT = "/ping";
const APP_PING_ANSWER_PREFIX = "/ping answer";
const APP_PING_INTERVAL_MS = 10_000;
let appPingIntervalId: ReturnType<typeof setInterval> | null = null;
let unansweredPingSince: number | null = null;
let registrationCheckInFlight = false;
let documentHiddenAt: number | null = null;

const isAppPingResponse = (msg: unknown): boolean => {
  if (!msg || typeof msg !== "object") return false;
  const text = (msg as { text?: unknown }).text;
  return typeof text === "string" && text.startsWith(APP_PING_ANSWER_PREFIX);
};

const markAppPingAcked = () => {
  unansweredPingSince = null;
};

const stopAppPingInterval = () => {
  if (appPingIntervalId) {
    clearInterval(appPingIntervalId);
    appPingIntervalId = null;
  }
  unansweredPingSince = null;
};

/**
 * Emits an application-level `/ping` over the socket (NOT an HTTP request).
 * Backend replies with a socket message `{ text: '/ping answer: ok', ... }`.
 *
 * Must include `chat.id` — the backend message handler reads the user from
 * `data['chat']['id']` and raises before reaching the `/ping` branch otherwise.
 * Returns true if the ping was emitted.
 */
export const sendSocketPing = (): boolean => {
  if (isSessionActivationInFlight()) {
    console.debug("[Socket] ping skipped: session activation in flight");
    return false;
  }
  if (!socketInstance?.connected) {
    console.debug("[Socket] ping skipped: socket not connected");
    return false;
  }

  const { connectionError, deviceConflictModalOpen, domain, focus } =
    useAppStore.getState();
  if (connectionError || deviceConflictModalOpen) {
    console.debug("[Socket] ping skipped: connection blocked");
    return false;
  }
  if (!domain || !focus) {
    console.debug("[Socket] ping skipped: missing domain/focus", {
      domain,
      focus,
    });
    return false;
  }

  const userId = useUserStore.getState().user?.id;
  if (!userId) {
    console.debug("[Socket] ping skipped: missing user id");
    return false;
  }

  // Backend contract + actual handler shape (chat.id is required before /ping).
  const pingPayload = {
    user_id: userId,
    chat: {
      id: userId,
      type: "private" as const,
    },
    domain,
    focus,
    text: APP_PING_TEXT,
  };
  console.log('[Socket] emit "message" (ping):', pingPayload);
  socketInstance.emit("message", pingPayload);
  unansweredPingSince ??= Date.now();
  return true;
};

/**
 * Tear down and reconnect so the backend assigns a fresh sid.
 * Used only after explicit server rejection or confirmed missing registration.
 */
const forceReconnectSocket = (reason: string) => {
  if (!socketInstance) return;
  if (serverDisconnectVerificationInFlight) return;
  if (isSessionActivationInFlight()) return;

  const { connectionError, deviceConflictModalOpen, domain, focus } =
    useAppStore.getState();
  if (connectionError || deviceConflictModalOpen) return;

  const token = useUserStore.getState().token;
  if (!token || !domain || !focus) return;

  console.warn(`[Socket] force reconnect: ${reason}`);
  unansweredPingSince = null;

  applySocketAuth(socketInstance, token, focus, domain);
  if (socketInstance.io?.opts) {
    socketInstance.io.opts.reconnection = true;
  }
  if (socketInstance.connected) {
    socketInstance.disconnect();
  }

  scheduleSocketReconnect(() => {
    if (!socketInstance || socketInstance.connected) return;
    if (isSessionActivationInFlight()) return;

    const {
      connectionError: latestError,
      deviceConflictModalOpen: latestConflict,
      domain: latestDomain,
      focus: latestFocus,
    } = useAppStore.getState();
    const latestToken = useUserStore.getState().token;
    if (
      latestError ||
      latestConflict ||
      serverDisconnectVerificationInFlight ||
      !latestToken ||
      !latestDomain ||
      !latestFocus
    ) {
      return;
    }

    applySocketAuth(socketInstance, latestToken, latestFocus, latestDomain);
    if (socketInstance.io?.opts) {
      socketInstance.io.opts.reconnection = true;
    }
    socketInstance.connect();
  }, 300);
};

// Missing application replies are a latency signal, never proof of disconnection.
export const verifySocketRegistration = async () => {
  const socket = socketInstance;
  if (
    !socket?.connected ||
    registrationCheckInFlight ||
    isSessionActivationInFlight()
  ) return;
  const checkedSid = socket.id;
  registrationCheckInFlight = true;
  try {
    const { status } = await checkSessionStatus(checkedSid);
    if (isSessionActivationInFlight()) return;
    const action = sessionRecoveryAction(
      status, checkedSid, socketInstance?.id, Boolean(socketInstance?.connected),
    );
    if (action === "reconnect") {
      forceReconnectSocket("server confirmed unregistered session");
    } else if (action === "conflict") {
      openDeviceConflictModal();
      disableSocketReconnection(socket);
    }
  } catch {
    // A timeout may mean a busy worker or network loss. Transport heartbeats
    // handle failure; keep the existing connection and the user's answer.
  } finally {
    registrationCheckInFlight = false;
  }
};

/**
 * Call right after HTTP endpoints that expect a socket follow-up
 * (`/button_press`, `/interaction_submit`, etc.). Emits a ping so the backend
 * gets a fresh socket round-trip exactly when it is about to push a response.
 * Never tears the socket down.
 */
export const pingSocketAfterSubmit = () => {
  sendSocketPing();
};

const startAppPingInterval = () => {
  stopAppPingInterval();
  sendSocketPing();
  appPingIntervalId = setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (!socketInstance?.connected) return; // real disconnects handled elsewhere

    if (shouldVerifySocket(unansweredPingSince, Date.now())) {
      void verifySocketRegistration();
    }
    sendSocketPing();
  }, APP_PING_INTERVAL_MS);
};

const isDeviceConflictText = (text: string): boolean =>
  text.includes("Close SeeKnowledge") ||
  text.includes("Close the application") ||
  text.includes("another device") ||
  text.includes("other device");

const clearBrowserLockQuietly = () => {
  const { setIsBrowserLocked, isBrowserLocked } = useAppStore.getState();
  if (isBrowserLocked) {
    void setIsBrowserLocked(false, false);
  }
};

const openDeviceConflictModal = () => {
  const { setDeviceConflictModalOpen, setConnectionError, setIsAssessmentOpen } =
    useAppStore.getState();
  const { clearStore, setError } = useAssessmentStore.getState();
  clearStore();
  setError(null);
  setIsAssessmentOpen(false);
  clearBrowserLockQuietly();
  setConnectionError(null);
  setDeviceConflictModalOpen(true);
};

const disableSocketReconnection = (socket: Socket) => {
  socket.disconnect();
  if (socket.io?.opts) {
    socket.io.opts.reconnection = false;
  }
};

export const getSocketInstance = (): Socket | null => {
  return socketInstance;
};

export const disconnectSocket = () => {
  // console.log("[Socket] disconnectSocket() called");
  console.trace("[Socket] disconnectSocket stack trace");
  cancelPendingSocketReconnects();
  stopAppPingInterval();
  if (socketInstance) {
    socketInstance.auth = {};
    socketInstance.disconnect();
    if (socketInstance.io && socketInstance.io.opts) {
      socketInstance.io.opts.reconnection = false;
    }
  }
};

export const useSocket = (
  setMessages: SetMessages,
  token: string,
  onStreamingMessageTrigger: () => void,
  setRefreshScheduled: (isScheduled: boolean) => void,
  setScheduledTargetMessageId: (messageId: string | null) => void,
) => {
  const {
    setIsBrowserLocked,
    setPlaySoundAnswerIsCorrect,
    setShowLessonOnboarding,
    setLessonDialogue,
    setLessonDialogueMetadata,
    setRealtimePersona,
    setRealtimeMaturityLevel,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    triggerStartRecording,
    domain,
    focus,
    setConnectionError,
    setDeviceConflictModalOpen,
  } = useAppStore();

  const { setTargetMessageId, setWaitForAnimation } = useChatStore();

  const socketRef = useRef<Socket | null>(null);
  const streamingCheckIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagePollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageReceivedTimeRef = useRef<number>(Date.now());
  const consecutiveEmptyResultsRef = useRef<number>(0);
  const disconnectedPollingStartTimeRef = useRef<number | null>(null);
  // Guards for /pagination?direction=middle streaming reconciliation (prevents infinite polling)
  const streamingMiddlePollStartRef = useRef<number | null>(null);
  const streamingMiddlePollAttemptsRef = useRef<number>(0);
  const streamingMiddlePollNoChangeRef = useRef<number>(0);
  const streamingMiddlePollLastFingerprintRef = useRef<string | null>(null);
  const syncMissedMessagesRef = useRef<(() => Promise<void>) | null>(null);

  // Batch streaming updates to reduce CPU load
  const activeStreamingIdRef = useRef<string | null>(null);
  const pendingStreamingDataRef = useRef<Message | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const focusRef = useRef(focus);
  const domainRef = useRef(domain);
  const tokenRef = useRef(token);
  // Updated only after the connect effect decides — do not use tokenRef here
  // (tokenRef is synced in an earlier effect, which made tokenChanged always false).
  const previousTokenForConnectRef = useRef<string | null>(token || null);
  const previousFocusRef = useRef(focus);
  const previousDomainRef = useRef(domain);

  const mergeTabbedContent = (
    existingTabs: Message["tabbed_content"],
    incomingTabs: Message["tabbed_content"],
  ): Message["tabbed_content"] => {
    const existing = Array.isArray(existingTabs) ? existingTabs : [];
    const incoming = Array.isArray(incomingTabs) ? incomingTabs : [];
    if (!incoming.length) return existing;

    const normalizeLabel = normalizeTabLabel;

    const findMatchingExistingIndex = (
      source: Message["tabbed_content"],
      incomingTab: any,
    ): number => {
      const sourceTabs = Array.isArray(source) ? source : [];
      const incomingId = String(incomingTab?.tab_id ?? "");
      const incomingLabel = normalizeLabel(incomingTab?.tab_label);

      const idMatchIndex = sourceTabs.findIndex((existingTab: any) => {
        const existingId = String(existingTab?.tab_id ?? "");
        const existingLabel = normalizeLabel(existingTab?.tab_label);

        // Always match by tab_id when both have IDs (avoids merging tabs with same label but different ids)
        if (incomingId && existingId && existingId === incomingId) {
          if (
            incomingLabel &&
            existingLabel &&
            incomingLabel !== existingLabel &&
            countTabsWithBaseId(sourceTabs, incomingId) > 1
          ) {
            // Legacy duplicate tab_id: multiple tabs share one backend id — match by label.
            return incomingLabel === existingLabel;
          }
          return true;
        }

        // Only match by label when incoming has no id (e.g. streaming chunk with placeholder)
        if (
          !incomingId &&
          incomingLabel &&
          existingLabel &&
          existingLabel === incomingLabel
        ) {
          return true;
        }

        // Match deduped UI ids back to raw backend ids (label may change on tab edits).
        if (incomingId && existingId && tabsShareBaseId(incomingId, existingId)) {
          if (
            incomingLabel &&
            existingLabel &&
            incomingLabel !== existingLabel &&
            countTabsWithBaseId(sourceTabs, incomingId) > 1
          ) {
            return incomingLabel === existingLabel;
          }
          return true;
        }

        return false;
      });
      if (idMatchIndex >= 0) return idMatchIndex;

      // Unique label fallback: same tab resent with a new id (multi-submit /
      // reconnect) must merge, not append — otherwise the tab strip duplicates.
      if (!incomingLabel) return -1;
      let matchIndex = -1;
      let matchCount = 0;
      for (let i = 0; i < sourceTabs.length; i++) {
        if (normalizeLabel(sourceTabs[i]?.tab_label) === incomingLabel) {
          matchCount += 1;
          matchIndex = i;
        }
      }
      return matchCount === 1 ? matchIndex : -1;
    };

    const mergeTab = (existingTab: any, incomingTab: any) => {
      const incomingId = String(incomingTab?.tab_id ?? "");
      return {
        ...existingTab,
        ...incomingTab,
        // Keep original id if incoming update has empty/flaky id.
        tab_id: incomingId || existingTab?.tab_id || "",
      };
    };

    // Preserve previous behavior for partial streaming updates.
    // For full snapshots (incoming has at least as many tabs as existing), apply backend order.
    const isLikelyFullSnapshot = incoming.length >= existing.length;

    if (!isLikelyFullSnapshot) {
      const merged = [...existing];
      incoming.forEach((incomingTab: any) => {
        if (incomingTab == null || typeof incomingTab !== "object") return;
        const existingIdx = findMatchingExistingIndex(merged, incomingTab);

        if (existingIdx === -1) {
          merged.push(incomingTab);
          return;
        }

        merged[existingIdx] = mergeTab(merged[existingIdx], incomingTab);
      });

      return ensureUniqueTabIds(merged);
    }

    const matchedExistingIndexes = new Set<number>();
    const reordered = incoming.reduce<any[]>((acc, incomingTab) => {
      if (incomingTab == null || typeof incomingTab !== "object") return acc;
      const existingIdx = findMatchingExistingIndex(existing, incomingTab);

      if (existingIdx === -1) {
        acc.push(incomingTab);
        return acc;
      }

      matchedExistingIndexes.add(existingIdx);
      acc.push(mergeTab(existing[existingIdx], incomingTab));
      return acc;
    }, []);

    existing.forEach((tab, idx) => {
      if (matchedExistingIndexes.has(idx)) return;
      const existingId = String(tab?.tab_id ?? "");
      const existingLabel = normalizeLabel(tab?.tab_label);
      const supersededByIncoming =
        (Boolean(existingId) &&
          incoming.some((inTab) => {
            const incomingId = String(inTab?.tab_id ?? "");
            return incomingId && tabsShareBaseId(incomingId, existingId);
          })) ||
        (Boolean(existingLabel) &&
          incoming.some(
            (inTab) => normalizeLabel(inTab?.tab_label) === existingLabel,
          ));
      if (!supersededByIncoming) {
        reordered.push(tab);
      }
    });

    return ensureUniqueTabIds(reordered);
  };

  const getTabsFingerprint = (tabs: Message["tabbed_content"]): string => {
    if (!Array.isArray(tabs) || tabs.length === 0) return "";
    const validTabs = tabs.filter(
      (tab): tab is NonNullable<typeof tab> =>
        tab != null && typeof tab === "object",
    );
    return JSON.stringify(
      validTabs.map((tab) => ({
        id: tab.tab_id,
        label: tab.tab_label,
        content: tab.content ?? "",
        blocksLen: tab.content_blocks?.length ?? 0,
      })),
    );
  };

  useEffect(() => {
    focusRef.current = focus;
    domainRef.current = domain;
    tokenRef.current = token;
  }, [focus, domain, token]);

  useEffect(() => {
    const SOCKET_URL =
      process.env.REACT_APP_SOCKET_URL || window.location.origin;

    if (!socketInstance) {
      socketInstance = io(SOCKET_URL, {
        autoConnect: false,
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        upgrade: false,
      } as any);


    }

    const socket = socketInstance;
    socketRef.current = socket;

    const handleBeforeUnload = () => {
      if (socketInstance && socketInstance.connected) {
        socketInstance.disconnect();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    if (!listenersAttached && socket) {
      listenersAttached = true;

      socket.on("connect", () => {
        // Reset polling counters on connect
        consecutiveEmptyResultsRef.current = 0;
        disconnectedPollingStartTimeRef.current = null;

        void import("./internetConnection").then(
          ({ checkConnectivityAndReload }) => {
            void checkConnectivityAndReload();
          },
        );

        // Stop streaming reconciliation polling on connect
        if (streamingCheckIntervalRef.current) {
          clearInterval(streamingCheckIntervalRef.current);
          streamingCheckIntervalRef.current = null;
        }

        const { connectionError, setCurrentSocketId } = useAppStore.getState();
        if (connectionError) {
          setConnectionError(null);
        }

        if (socket.id) {
          setCurrentSocketId(socket.id);
        }

        const currentToken = tokenRef.current;
        const currentFocus = focusRef.current;
        const currentDomain = domainRef.current;
        if (currentToken && currentFocus && currentDomain) {
          applySocketAuth(socket, currentToken, currentFocus, currentDomain);
        }

        flushPendingMessages((msg) => socket.emit("message", msg));
        markSocketConnected();
        startAppPingInterval();
        socket.emit("session_ready");
        scheduleMissedMessageSyncAfterReconnect(
          () => syncMissedMessagesRef.current?.() ?? Promise.resolve(),
        );
      });

      socket.on("disconnect", (reason) => {
        // console.log("[Socket] Disconnected, reason:", reason);
        console.trace("[Socket] Disconnect stack trace");
        stopAppPingInterval();

        // Always stop any streaming reconciliation polling on disconnect
        if (streamingCheckIntervalRef.current) {
          clearInterval(streamingCheckIntervalRef.current);
          streamingCheckIntervalRef.current = null;
        }
        streamingMiddlePollStartRef.current = null;
        streamingMiddlePollAttemptsRef.current = 0;
        streamingMiddlePollNoChangeRef.current = 0;
        streamingMiddlePollLastFingerprintRef.current = null;

        setMessages((prev) => {
          const updated = [...prev];
          let hasChanges = false;

          updated.forEach((msg, idx) => {
            if (msg.isStreaming && !msg.message_id?.startsWith("temp-")) {
              hasChanges = true;
              updated[idx] = {
                ...msg,
                isStreaming: false,
                isForStreaming: false,
              };
            }
          });

          return hasChanges ? updated : prev;
        });

        // Chat lock unlocks via socket `is_browser_locked: false`. If the
        // transport dies first (stale sid / 401), unlock so mobile is not stuck.
        const {
          connectionError,
          isAssessmentOpen,
          isBrowserLocked,
        } = useAppStore.getState();
        if (isBrowserLocked && !isAssessmentOpen) {
          clearBrowserLockQuietly();
        }

        if (connectionError) {
          if (socket.io && socket.io.opts) {
            socket.io.opts.reconnection = false;
          }
          return;
        }

        // "io server disconnect" is both a real device-conflict kick and what
        // clients often see when the backend restarts. Verify via /session_status
        // before showing the conflict modal; retry while the backend is down.
        if (reason === "io server disconnect") {
          if (!isAdminRoute()) {
            serverDisconnectVerificationInFlight = true;
            disableSocketReconnection(socket);

            // Must use the pre-disconnect sid. Sid-less /session_status often
            // returns "active" because the *other* device is active, which
            // wrongly reconnects this client and reclaims the contest lock.
            const previousSid =
              useAppStore.getState().currentSocketId || undefined;

            const {
              setDeviceConflictModalOpen,
              setConnectionError,
              setIsAssessmentOpen,
              setIsBrowserLocked,
            } = useAppStore.getState();

            const SERVER_DISCONNECT_STATUS_MAX_ATTEMPTS = 20;
            const SERVER_DISCONNECT_STATUS_RETRY_MS = 1000;

            const surrenderContestOnKick = () => {
              const { clearStore, setError } = useAssessmentStore.getState();
              clearStore();
              setError(null);
              setIsAssessmentOpen(false);
              void setIsBrowserLocked(false, false);
            };

            const resumeAfterServerDisconnect = () => {
              serverDisconnectVerificationInFlight = false;
              setDeviceConflictModalOpen(false);
              setConnectionError(null);

              if (
                !tokenRef.current ||
                !focusRef.current ||
                !domainRef.current
              ) {
                return;
              }

              applySocketAuth(
                socket,
                tokenRef.current,
                focusRef.current,
                domainRef.current,
              );
              if (socket.io?.opts) {
                socket.io.opts.reconnection = true;
              }
              if (!socket.connected) {
                socket.connect();
              }
            };

            const confirmServerDisconnect = async (
              httpAttempt = 0,
              consecutiveActive = 0,
            ): Promise<void> => {
              try {
                const sessionStatus = await checkSessionStatus(previousSid);

                if (sessionStatus.status === "conflict") {
                  serverDisconnectVerificationInFlight = false;
                  surrenderContestOnKick();
                  openDeviceConflictModal();
                  return;
                }

                if (sessionStatus.status === "unregistered") {
                  resumeAfterServerDisconnect();
                  return;
                }

                if (sessionStatus.status === "active") {
                  const nextActive = consecutiveActive + 1;
                  // Two consecutive actives avoids racing a real kick that
                  // briefly looks clear while session state settles.
                  if (nextActive < 2) {
                    await new Promise((resolve) => setTimeout(resolve, 400));
                    return confirmServerDisconnect(httpAttempt, nextActive);
                  }

                  resumeAfterServerDisconnect();
                  return;
                }

                if (httpAttempt + 1 < SERVER_DISCONNECT_STATUS_MAX_ATTEMPTS) {
                  await new Promise((resolve) =>
                    setTimeout(resolve, SERVER_DISCONNECT_STATUS_RETRY_MS),
                  );
                  return confirmServerDisconnect(httpAttempt + 1, 0);
                }

                console.warn(
                  "[Socket] session_status ambiguous after server disconnect — reconnecting",
                );
                resumeAfterServerDisconnect();
              } catch (error) {
                // Backend is often still restarting; keep retrying instead of
                // leaving reconnection permanently disabled.
                if (httpAttempt + 1 < SERVER_DISCONNECT_STATUS_MAX_ATTEMPTS) {
                  await new Promise((resolve) =>
                    setTimeout(resolve, SERVER_DISCONNECT_STATUS_RETRY_MS),
                  );
                  return confirmServerDisconnect(httpAttempt + 1, 0);
                }

                console.warn(
                  "[Socket] session_status failed after server disconnect — reconnecting",
                  error,
                );
                resumeAfterServerDisconnect();
              }
            };

            void confirmServerDisconnect();
          } else {
            if (socket.io?.opts) {
              socket.io.opts.reconnection = true;
            }
            if (
              !socket.connected &&
              tokenRef.current &&
              focusRef.current &&
              domainRef.current
            ) {
              applySocketAuth(
                socket,
                tokenRef.current,
                focusRef.current,
                domainRef.current,
              );
              socket.connect();
            }
          }
          return;
        }

        const shouldReconnect =
          reason === "transport close" ||
          reason === "transport error" ||
          reason === "ping timeout";

        if (shouldReconnect) {
          const { connectionError: errorCheck, deviceConflictModalOpen } =
            useAppStore.getState();
          if (
            errorCheck ||
            deviceConflictModalOpen ||
            serverDisconnectVerificationInFlight
          ) {
            if (socket.io && socket.io.opts) {
              socket.io.opts.reconnection = false;
            }
            return;
          }

          // console.log("Attempting to reconnect after disconnect:", reason);
          const currentToken = tokenRef.current;
          if (currentToken && !socket.connected) {
            const reconnectDelay = reason === "ping timeout" ? 500 : 1000;
            scheduleSocketReconnect(() => {
              const { connectionError: finalErrorCheck } =
                useAppStore.getState();
              if (
                finalErrorCheck ||
                !tokenRef.current ||
                !focusRef.current ||
                !domainRef.current
              ) {
                if (socket.io && socket.io.opts) {
                  socket.io.opts.reconnection = false;
                }
                return;
              }

              if (!socket.connected && tokenRef.current) {
                // console.log("Manual reconnect attempt");
                applySocketAuth(
                  socket,
                  tokenRef.current,
                  focusRef.current,
                  domainRef.current,
                );
                const onReconnectAfterTimeout = () => {
                  scheduleMissedMessageSyncAfterReconnect(
                    () =>
                      syncMissedMessagesRef.current?.() ?? Promise.resolve(),
                  );
                  socket.off("connect", onReconnectAfterTimeout);
                };
                socket.once("connect", onReconnectAfterTimeout);
                socket.connect();
              }
            }, reconnectDelay);
          }
        }
      });

      socket.on("connect_error", (err: any) => {
        console.error("Socket connect_error:", err);
        console.error("Error type:", typeof err);
        console.error("Error keys:", err ? Object.keys(err) : "no keys");
        console.error("Error message:", err?.message);
        console.error("Error data:", err?.data);

        let connectionErrorMsg: string | null = null;
        let isDeviceConflict = false;

        if (err?.data?.connection_error) {
          connectionErrorMsg = err.data.connection_error;
        } else if (err?.connection_error) {
          connectionErrorMsg = err.connection_error;
        } else if (err?.message?.connection_error) {
          connectionErrorMsg = err.message.connection_error;
        } else if (
          err?.data &&
          typeof err.data === "string" &&
          err.data.includes("Close SeeKnowledge")
        ) {
          connectionErrorMsg = err.data;
        } else if (err?.message && typeof err.message === "string") {
          if (
            err.message.includes("Close SeeKnowledge") ||
            err.message.includes("Close the application")
          ) {
            connectionErrorMsg = err.message;
          }
        }

        // Check if this is a device conflict error
        const errorText =
          connectionErrorMsg || err?.message || err?.data?.message || "";
        if (typeof errorText === "string" && isDeviceConflictText(errorText)) {
          isDeviceConflict = true;
        }

        if (isDeviceConflict) {
          if (!navigator.onLine) {
            setConnectionError(null);
            return;
          }
          if (!isAdminRoute() && !isSessionActivationInFlight()) {
            openDeviceConflictModal();
          }
          disableSocketReconnection(socket);
          return;
        }

        if (!navigator.onLine) {
          setConnectionError(null);
          return;
        }

        // Connection rejected without SeeKnowledge text (python-socketio
        // return False). Sid-less /session_status is conflict when another
        // device holds the only registered sid(s).
        if (!isAdminRoute() && !isSessionActivationInFlight()) {
          void checkSessionStatus()
            .then((status) => {
              if (
                status.status === "conflict" &&
                !isSessionActivationInFlight()
              ) {
                openDeviceConflictModal();
                disableSocketReconnection(socket);
              }
            })
            .catch(() => {});
        }

        if (socket.io?.opts) {
          socket.io.opts.reconnection = true;
        }
      });

      socket.on("connection_error", (data: any) => {
        // console.log("Received connection_error event:", data);
        // console.log("Connection_error data type:", typeof data);
        // console.log(
        //   "Connection_error data keys:",
        //   data ? Object.keys(data) : "no keys"
        // );

        let errorMessage: string | null = null;

        if (data?.connection_error) {
          errorMessage = data.connection_error;
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (typeof data === "string") {
          errorMessage = data;
        }

        const finalErrorMessage =
          errorMessage &&
          typeof errorMessage === "string" &&
          errorMessage.trim()
            ? errorMessage
            : "Connection lost. Please check your internet connection.";

        const errorText = finalErrorMessage || "";
        const isDeviceConflict = isDeviceConflictText(errorText);

        if (isDeviceConflict) {
          if (!navigator.onLine) {
            setConnectionError(null);
            return;
          }
          if (!isAdminRoute() && !isSessionActivationInFlight()) {
            openDeviceConflictModal();
          }
          disableSocketReconnection(socket);
          return;
        }

        if (!navigator.onLine) {
          setConnectionError(null);
          return;
        }

        if (socket.io?.opts) {
          socket.io.opts.reconnection = true;
        }
      });

      // Also listen for error event which might contain connection_error
      socket.on("error", (data: any) => {
        // console.log("Received error event:", data);
        if (
          data?.connection_error ||
          (data?.message &&
            typeof data.message === "string" &&
            data.message.includes("Close SeeKnowledge"))
        ) {
          const errorMessage = data?.connection_error || data?.message;
          if (errorMessage) {
            const errorText =
              typeof errorMessage === "string" ? errorMessage : "";
            const isDeviceConflict = isDeviceConflictText(errorText);

            if (isDeviceConflict) {
              if (!isAdminRoute() && !isSessionActivationInFlight()) {
                openDeviceConflictModal();
              }
              disableSocketReconnection(socket);
            }
          }
        }
      });

      socket.on("reconnect_attempt", (attemptNumber) => {
        // console.log(`Reconnection attempt ${attemptNumber}`);
        const { connectionError } = useAppStore.getState();
        if (connectionError) {
          socket.disconnect();
          if (socket.io && socket.io.opts) {
            socket.io.opts.reconnection = false;
          }
          return;
        }

        const currentToken = tokenRef.current;
        if (currentToken && focusRef.current && domainRef.current) {
          applySocketAuth(
            socket,
            currentToken,
            focusRef.current,
            domainRef.current,
          );
        }
      });

      socket.on("reconnect_failed", () => {
        console.error("Socket reconnect_failed - attempting manual reconnect");
        const currentToken = tokenRef.current;
        if (
          currentToken &&
          !socket.connected &&
          focusRef.current &&
          domainRef.current
        ) {
          applySocketAuth(
            socket,
            currentToken,
            focusRef.current,
            domainRef.current,
          );
          scheduleSocketReconnect(() => {
            if (
              !socket.connected &&
              tokenRef.current &&
              focusRef.current &&
              domainRef.current
            ) {
              applySocketAuth(
                socket,
                tokenRef.current,
                focusRef.current,
                domainRef.current,
              );
              socket.connect();
            }
          }, 2000);
        }
      });

      const handleStreamingReconnect = async () => {
        try {
          const currentMessages = await new Promise<Message[]>((resolve) => {
            setMessages((prevMessages) => {
              resolve(prevMessages);
              return prevMessages;
            });
          });

          if (!currentMessages.length) {
            return;
          }

          const streamingMessage = currentMessages.find(
            (msg) => msg.isStreaming && !msg.message_id?.startsWith("temp-"),
          );

          if (streamingMessage) {
            if (streamingCheckIntervalRef.current) {
              clearInterval(streamingCheckIntervalRef.current);
              streamingCheckIntervalRef.current = null;
            }

            // Reset guard counters for this reconciliation session
            streamingMiddlePollStartRef.current = Date.now();
            streamingMiddlePollAttemptsRef.current = 0;
            streamingMiddlePollNoChangeRef.current = 0;
            streamingMiddlePollLastFingerprintRef.current = null;

            const checkAndUpdateStreaming = async () => {
              try {
                streamingMiddlePollAttemptsRef.current += 1;
                const startedAt =
                  streamingMiddlePollStartRef.current ?? Date.now();
                const elapsedMs = Date.now() - startedAt;

                // Hard stop: avoid hammering backend forever if message stays "streaming" after backend restart
                if (
                  elapsedMs > 30_000 ||
                  streamingMiddlePollAttemptsRef.current > 30
                ) {
                  setMessages((prev) => {
                    const idx = prev.findIndex(
                      (m) => m?.message_id === streamingMessage.message_id,
                    );
                    if (idx === -1) return prev;
                    const updated = [...prev];
                    updated[idx] = {
                      ...updated[idx],
                      isStreaming: false,
                      isForStreaming: false,
                    };
                    return updated;
                  });
                  return false;
                }

                const updatedMessages = await paginateMessages({
                  message_id: streamingMessage.message_id,
                  direction: "middle",
                  number: 1,
                });

                if (updatedMessages.length > 0) {
                  const updatedMessage = updatedMessages[0];

                  const fingerprint = `${Boolean(updatedMessage.isStreaming)}|${updatedMessage.text ?? ""}|${updatedMessage.title ?? ""}`;
                  if (
                    streamingMiddlePollLastFingerprintRef.current ===
                    fingerprint
                  ) {
                    streamingMiddlePollNoChangeRef.current += 1;
                  } else {
                    streamingMiddlePollNoChangeRef.current = 0;
                    streamingMiddlePollLastFingerprintRef.current = fingerprint;
                  }

                  // If backend keeps returning identical payload, stop reconciliation (no progress)
                  if (streamingMiddlePollNoChangeRef.current >= 5) {
                    setMessages((prev) => {
                      const idx = prev.findIndex(
                        (m) => m?.message_id === streamingMessage.message_id,
                      );
                      if (idx === -1) return prev;
                      const updated = [...prev];
                      updated[idx] = {
                        ...updated[idx],
                        isStreaming: false,
                        isForStreaming: false,
                      };
                      return updated;
                    });
                    return false;
                  }

                  setMessages((prev) => {
                    const updated = [...prev];
                    const existingIdx = updated.findIndex(
                      (m) => m?.message_id === updatedMessage?.message_id,
                    );
                    if (existingIdx !== -1) {
                      const existing = updated[existingIdx];
                      if (
                        existing.isStreaming ||
                        updatedMessage.isStreaming ||
                        updatedMessage.text !== existing.text
                      ) {
                        updated[existingIdx] = {
                          ...existing,
                          ...updatedMessage,
                          isStreaming:
                            updatedMessage.isStreaming ?? existing.isStreaming,
                        };
                      }
                    }
                    return updated;
                  });

                  if (updatedMessage.isStreaming) {
                    return true;
                  } else {
                    if (streamingCheckIntervalRef.current) {
                      clearInterval(streamingCheckIntervalRef.current);
                      streamingCheckIntervalRef.current = null;
                    }
                    return false;
                  }
                }
                return false;
              } catch (error) {
                console.error("Error checking streaming message:", error);
                // Backoff/stop on repeated errors to avoid backend load loops
                if (streamingMiddlePollAttemptsRef.current > 5) {
                  return false;
                }
                return true;
              }
            };

            const stillStreaming = await checkAndUpdateStreaming();

            if (stillStreaming) {
              streamingCheckIntervalRef.current = setInterval(async () => {
                const continueChecking = await checkAndUpdateStreaming();
                if (!continueChecking && streamingCheckIntervalRef.current) {
                  clearInterval(streamingCheckIntervalRef.current);
                  streamingCheckIntervalRef.current = null;
                }
              }, 1000);
            }
          }

          const lastMessage = currentMessages[currentMessages.length - 1];
          const lastMessageId = lastMessage?.message_id;

          if (lastMessageId && !lastMessageId.startsWith("temp-")) {
            const missedMessages = await paginateMessages({
              message_id: lastMessageId,
              direction: "down",
              number: 50,
            });

            if (missedMessages.length > 0) {
              setMessages((prev) => {
                const updated = [...prev];
                let hasChanges = false;

                missedMessages.forEach((incomingMsg) => {
                  const existingIdx = updated.findIndex(
                    (m) => m?.message_id === incomingMsg?.message_id,
                  );

                  if (existingIdx !== -1) {
                    const existingMsg = updated[existingIdx];
                    const mergedTabs =
                      incomingMsg.type === "tabbed_message"
                        ? mergeTabbedContent(
                            existingMsg.tabbed_content,
                            incomingMsg.tabbed_content,
                          )
                        : (incomingMsg.tabbed_content ??
                          existingMsg.tabbed_content);

                    const shouldUpdate =
                      existingMsg.text !== incomingMsg.text ||
                      existingMsg.title !== incomingMsg.title ||
                      existingMsg.isStreaming !==
                        (incomingMsg.isStreaming ?? existingMsg.isStreaming) ||
                      getTabsFingerprint(existingMsg.tabbed_content) !==
                        getTabsFingerprint(mergedTabs);

                    if (shouldUpdate) {
                      updated[existingIdx] = {
                        ...existingMsg,
                        ...incomingMsg,
                        tabbed_content: mergedTabs,
                        isStreaming:
                          incomingMsg.isStreaming ?? existingMsg.isStreaming,
                      };
                      hasChanges = true;
                    }
                    return;
                  }

                  updated.push(incomingMsg);
                  hasChanges = true;
                });

                return hasChanges ? updated : prev;
              });
            }
          }
        } catch (error) {
          console.error("Error fetching missed messages on reconnect:", error);
        }
      };

      syncMissedMessagesRef.current = handleStreamingReconnect;

      socket.on("reconnect", (attemptNumber) => {
        // Reset polling counters on reconnect
        consecutiveEmptyResultsRef.current = 0;
        disconnectedPollingStartTimeRef.current = null;

        const currentToken = tokenRef.current;
        if (currentToken && focusRef.current && domainRef.current) {
          applySocketAuth(
            socket,
            currentToken,
            focusRef.current,
            domainRef.current,
          );
        }
        // `connect` already schedules missed-message sync after reconnect.
      });

      const scheduleRefreshIfUnvisible = () => {
        if (document.visibilityState !== "visible" && isMobile) {
          setRefreshScheduled(true);
        }
      };

      const scheduleTargetIfUnvisible = (messageId: string | null) => {
        if (document.visibilityState !== "visible" && isMobile) {
          setScheduledTargetMessageId(messageId);
        }
      };

      // Flush batched streaming update
      const flushStreamingUpdate = () => {
        rafIdRef.current = null;
        const data = pendingStreamingDataRef.current;
        const messageId = activeStreamingIdRef.current;

        if (!data || !messageId) return;
        pendingStreamingDataRef.current = null;

        setMessages((prev) => {
          const idx = prev.findIndex((m) => m?.message_id === messageId);
          if (idx === -1) return prev;

          const existing = prev[idx];
          const mergedTabs =
            data.type === "tabbed_message"
              ? mergeTabbedContent(existing.tabbed_content, data.tabbed_content)
              : (data.tabbed_content ?? existing.tabbed_content);

          if (
            existing.text === data.text &&
            existing.isStreaming === data.isStreaming &&
            existing.title === data.title &&
            getTabsFingerprint(existing.tabbed_content) ===
              getTabsFingerprint(mergedTabs)
          ) {
            return prev;
          }

          const updated = [...prev];
          updated[idx] = {
            ...existing,
            ...data,
            tabbed_content: mergedTabs,
            isPlaceholder: false,
            minHeightFull: false,
          };
          return updated;
        });
      };

      const replaceOrUpdateMessage = (msg: Message) => {
        if (!msg?.message_id) {
          return;
        }

        // Live updates only — historical messages with this flag must not
        // re-trigger stats fetches when the chat history mounts after reload.
        if (msg.refreshedLeaderBoard === true) {
          const { setRefreshedLeaderBoard, setRefreshedProgressBar } =
            useStatisticsStore.getState();
          void setRefreshedLeaderBoard(true);
          void setRefreshedProgressBar(true);
        }

        // Update last message received time
        lastMessageReceivedTimeRef.current = Date.now();

        const { shouldHideStopMessage, setShouldHideStopMessage } =
          useAppStore.getState();
        if (
          shouldHideStopMessage &&
          msg.is_bot &&
          msg.text &&
          msg.text.toLowerCase().includes("processing previous task stopped")
        ) {
          setShouldHideStopMessage(false);
          return;
        }

        // Batch streaming updates - avoid setMessages for subsequent chunks
        if (
          msg.isStreaming &&
          !msg.message_id.startsWith("temp-") &&
          msg.is_bot
        ) {
          if (activeStreamingIdRef.current === msg.message_id) {
            pendingStreamingDataRef.current = msg;
            if (!rafIdRef.current) {
              rafIdRef.current = requestAnimationFrame(flushStreamingUpdate);
            }
            return;
          }

          activeStreamingIdRef.current = msg.message_id;
          setMessages((prev) => {
            const phIdx = prev.findIndex((m) =>
              m?.message_id?.startsWith("temp-"),
            );
            if (phIdx !== -1) {
              const updated = [...prev];
              updated[phIdx] = {
                ...msg,
                isPlaceholder: false,
                minHeightFull: false,
              };
              const before = updated[phIdx - 1];
              if (before) onStreamingMessageTrigger();
              return updated;
            }
            const existingIdx = prev.findIndex(
              (m) => m?.message_id === msg.message_id,
            );
            if (existingIdx !== -1) {
              const updated = [...prev];
              const existingMsg = prev[existingIdx];
              const mergedTabs =
                msg.type === "tabbed_message" && msg.tabbed_content
                  ? mergeTabbedContent(
                      existingMsg.tabbed_content,
                      msg.tabbed_content,
                    )
                  : (msg.tabbed_content ?? existingMsg.tabbed_content);
              updated[existingIdx] = {
                ...existingMsg,
                ...msg,
                tabbed_content: mergedTabs,
                isPlaceholder: false,
                minHeightFull: false,
              };
              return updated;
            }
            return [...prev, msg];
          });
          return;
        }

        if (
          !msg.isStreaming &&
          activeStreamingIdRef.current === msg.message_id
        ) {
          activeStreamingIdRef.current = null;
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
          }
          pendingStreamingDataRef.current = null;
        }

        setMessages((prev) => {
          const updated = [...prev];

          if (!msg.is_bot && msg.isForStreaming) {
            updated.push(msg);
            const placeholder = createPlaceholderMessage();
            updated.push(placeholder);
            onStreamingMessageTrigger();
            return updated;
          }

          const existingIdx = updated.findIndex(
            (m) => m?.message_id === msg?.message_id,
          );

          if (existingIdx !== -1) {
            const existingMsg = updated[existingIdx];

            // Special handling for tabbed messages
            if (msg.type === "tabbed_message" && msg.tabbed_content) {
              const mergedTabs = mergeTabbedContent(
                existingMsg.tabbed_content,
                msg.tabbed_content,
              );

              updated[existingIdx] = {
                ...existingMsg,
                ...msg,
                tabbed_content: mergedTabs,
                isPlaceholder: false,
                minHeightFull: false,
              };

              if (msg.isStreaming || existingMsg.isStreaming) {
                const before = updated[existingIdx - 1];
                if (before) onStreamingMessageTrigger();
              }
              return updated;
            }

            if (msg.isStreaming || existingMsg.isStreaming) {
              updated[existingIdx] = {
                ...existingMsg,
                ...msg,
                isPlaceholder: false,
                minHeightFull: false,
              };
              const before = updated[existingIdx - 1];
              if (before) onStreamingMessageTrigger();
              return updated;
            }

            // Always update message if it exists - backend may send incremental updates
            updated[existingIdx] = { ...existingMsg, ...msg };
            return [...updated];
          }

          if (msg.isStreaming || msg.message_type === "suggestion") {
            const phIdx = updated.findIndex((m) =>
              m?.message_id?.startsWith("temp-"),
            );
            if (phIdx !== -1) {
              updated[phIdx] = {
                ...msg,
                isPlaceholder: false,
                minHeightFull: false,
              };
              const before = updated[phIdx - 1];
              if (before) onStreamingMessageTrigger();
              return updated;
            }
          }

          if (!msg.isStreaming) {
            const phIdx = updated.findIndex((m) =>
              m?.message_id?.startsWith("temp-"),
            );
            if (phIdx !== -1) {
              updated.splice(phIdx, 0, msg);
              return updated;
            }
          }

          return [...updated, msg];
        });
      };

      const handleSystemMessage = async (msg: SystemEventType) => {
        if ("is_browser_locked" in msg) {
          if (!msg.is_browser_locked) {
            setMessages((prev) => {
              const updated = [...prev];

              const placeholderIndex = updated.findIndex((m) =>
                m?.message_id?.startsWith("temp-"),
              );
              if (placeholderIndex !== -1) {
                updated.splice(placeholderIndex, 1);
              }
              return updated;
            });
          }
          const { setError } = useAssessmentStore.getState();
          if (msg.is_browser_locked) {
            setError("Close the application on another devices");
          } else {
            setError(null);
          }
          await setIsBrowserLocked(msg.is_browser_locked);
          return true;
        }
        if ("play_sound_answer_is_correct" in msg) {
          setPlaySoundAnswerIsCorrect(msg.play_sound_answer_is_correct);
          return true;
        }
        if ("play_sound_badge" in msg) {
          setPlaySoundAnswerIsCorrect(msg.play_sound_badge);
          return true;
        }
        if ("show_lesson_onboarding" in msg) {
          const { isAssessmentOpen, isPreassessmentOpen } =
            useAppStore.getState();
          if (
            msg.show_lesson_onboarding &&
            (isAssessmentOpen || isPreassessmentOpen)
          ) {
            return true;
          }
          setShowLessonOnboarding(msg.show_lesson_onboarding);
          return true;
        }
        if (
          "switch_to_lesson_dialogue" in msg &&
          msg.switch_to_lesson_dialogue
        ) {
          setLessonDialogue(true);

          if ("metadata" in msg) {
            setLessonDialogueMetadata(msg.metadata);
          }
          if (
            "persona" in msg &&
            typeof msg.persona === "object" &&
            msg.persona !== null
          ) {
            const persona = Object.assign(msg.persona, {
              description: "",
            }) as PersonaMenuItem;
            setRealtimePersona(persona);
          }
          if (
            "maturity_level" in msg &&
            typeof msg.maturity_level === "string"
          ) {
            setRealtimeMaturityLevel(msg.maturity_level);
          }

          if (
            "seconds_allowed" in msg &&
            typeof msg.seconds_allowed === "number"
          ) {
            setLessonSecondsAllowed(msg.seconds_allowed);
            setLessonSecondsRemaining(msg.seconds_allowed);
          }

          return true;
        }
        if ("scroll_to_message_id" in msg) {
          if (msg.constructor.name === "message") {
            setWaitForAnimation(true);
          }

          const { domain: targetDomain, focus: targetFocus } = msg;

          if (
            focusRef.current !== targetFocus ||
            domainRef.current !== targetDomain
          ) {
            const { isAssessmentOpen } = useAppStore.getState();
            if (!isAssessmentOpen) {
              setDomainFocus(targetDomain, targetFocus);
            }
          }

          setTargetMessageId(msg.scroll_to_message_id);
          scheduleTargetIfUnvisible(msg.scroll_to_message_id);
          return true;
        }
        if ("start_recording" in msg && msg.start_recording) {
          const startRecordingMsg = msg as SystemStartRecording;
          const { domain: targetDomain, focus: targetFocus } =
            startRecordingMsg;
          const { isAssessmentOpen, setDomainFocus: syncDomainFocus } =
            useAppStore.getState();

          if (!isAssessmentOpen) {
            if (
              focusRef.current !== targetFocus ||
              domainRef.current !== targetDomain
            ) {
              focusRef.current = targetFocus;
              domainRef.current = targetDomain;
              void syncDomainFocus(targetDomain, targetFocus);
            }
            triggerStartRecording();
          }
          return true;
        }
        return false;
      };

      const shouldIgnoreSocketMessage = (): boolean => {
        const {
          connectionError,
          deviceConflictModalOpen,
          setConnectionError: clearConnectionError,
        } = useAppStore.getState();

        if (deviceConflictModalOpen) {
          return true;
        }

        if (connectionError) {
          if (socket.connected) {
            clearConnectionError(null);
            return false;
          }
          return true;
        }

        return false;
      };

      socket.on("message", async (msg: SystemEventType) => {
        if (isAppPingResponse(msg)) {
          markAppPingAcked();
          return;
        }

        if (shouldIgnoreSocketMessage()) {
          console.warn("Ignoring message event - connection unavailable");
          return;
        }

        const isSystemMessage = await handleSystemMessage(msg);
        if (!isSystemMessage) {
          scheduleRefreshIfUnvisible();
          replaceOrUpdateMessage(msg as Message);
        }
      });

      socket.on("response", async (msg: SystemEventType) => {
        let msgAny = msg as any;

        if (Array.isArray(msgAny) && msgAny.length > 0) {
          msgAny = msgAny[0];
        }

        if (msgAny?.session_error === "session_unregistered") {
          forceReconnectSocket("server rejected unregistered session");
          return;
        }
        if (msgAny?.session_error === "session_replaced") {
          if (!isSessionActivationInFlight()) {
            openDeviceConflictModal();
            disableSocketReconnection(socket);
          }
          return;
        }

        // Backend second-device reject: emit {connection_error} then return False.
        // Must handle before shouldIgnoreSocketMessage / connect teardown.
        const conflictFromResponse =
          (typeof msgAny?.connection_error === "string" &&
            msgAny.connection_error) ||
          (typeof msgAny?.message === "string" &&
          isDeviceConflictText(msgAny.message)
            ? msgAny.message
            : null);
        if (
          conflictFromResponse &&
          !isAdminRoute() &&
          !isSessionActivationInFlight()
        ) {
          if (navigator.onLine) {
            openDeviceConflictModal();
          }
          disableSocketReconnection(socket);
          return;
        }

        if (isAppPingResponse(msgAny)) {
          markAppPingAcked();
          return;
        }

        if (shouldIgnoreSocketMessage()) {
          console.warn("Ignoring response event - connection unavailable");
          return;
        }

        if (msgAny.type === "assessment_list") {
          const assessments = normalizeAssessmentList(msgAny.assessments);
          if (assessments.length > 0) {
            const { setAvailableAssessments } = useAssessmentStore.getState();
            setAvailableAssessments(assessments);
          }
          return;
        }

        if (
          msgAny.type === "assessment" &&
          msgAny.time_limit &&
          msgAny.questions
        ) {
          try {
            if (!msgAny.assessment_id) {
              msgAny.assessment_id = `assessment_${Date.now()}`;
            }

            if (
              typeof msgAny.time_limit === "number" &&
              typeof msgAny.assessment_id === "string" &&
              Array.isArray(msgAny.questions) &&
              msgAny.questions.length > 0
            ) {
              const normalizedData = {
                time_limit: msgAny.time_limit,
                assessment_id: msgAny.assessment_id,
                title:
                  typeof msgAny.title === "string" && msgAny.title.trim()
                    ? msgAny.title.trim()
                    : undefined,
                questions: msgAny.questions.map((q: any, index: number) =>
                  normalizeAssessmentQuestion(q, index),
                ),
                total_questions_number: msgAny.total_questions_number,
                answered_questions_number: msgAny.answered_questions_number,
                scores_earned: msgAny.scores_earned,
                bonus_score: msgAny.bonus_score,
                mandatory_questions_number: msgAny.mandatory_questions_number,
                bonus_questions_number: msgAny.bonus_questions_number,
                mandatory_score: msgAny.mandatory_score,
                mandatory_max_score: msgAny.mandatory_max_score,
                bonus_question_score: msgAny.bonus_question_score,
                bonus_question_max_score: msgAny.bonus_question_max_score,
                user_score: msgAny.user_score,
                gamification_bonus_score: msgAny.gamification_bonus_score,
                contest_points: msgAny.contest_points,
                time_left:
                  msgAny.time_left !== undefined ? msgAny.time_left : null,
                total_time_spent:
                  typeof msgAny.total_time_spent === "number"
                    ? msgAny.total_time_spent
                    : null,
                current_user_place:
                  msgAny.current_user_place !== undefined
                    ? msgAny.current_user_place
                    : null,
                leaderboard: Array.isArray(msgAny.leaderboard)
                  ? msgAny.leaderboard
                      .filter(
                        (item: any) =>
                          item &&
                          typeof item.user_id === "string" &&
                          typeof item.name === "string" &&
                          (typeof item.score === "number" ||
                            typeof item.user_score === "number"),
                      )
                      .map((item: any) => ({
                        ...item,
                        score:
                          typeof item.score === "number"
                            ? item.score
                            : item.user_score,
                        user_score:
                          typeof item.user_score === "number"
                            ? item.user_score
                            : item.score,
                      }))
                  : undefined,
                gamification_enabled: resolveAssessmentGamificationEnabled(msgAny),
                answer_editing_enabled:
                  resolveAssessmentAnswerEditingEnabled(msgAny),
                show_feedback: resolveAssessmentShowFeedback(msgAny),
                previous_answers: Array.isArray(msgAny.previous_answers)
                  ? msgAny.previous_answers
                  : undefined,
              };

              const { setAssessmentData } = useAssessmentStore.getState();
              const { setIsAssessmentOpen } = useAppStore.getState();

              setAssessmentData(normalizedData);
              setIsAssessmentOpen(true);
              return;
            }
          } catch (error) {
            console.error(
              "Error handling assessment data from response:",
              error,
            );
          }
        }

        const isSystemMessage = await handleSystemMessage(msgAny);
        if (!isSystemMessage) {
          scheduleRefreshIfUnvisible();
          replaceOrUpdateMessage(msgAny as Message);
        }
      });

      socket.on("assessment", async (data: AssessmentData) => {
        try {
          const { connectionError, deviceConflictModalOpen } =
            useAppStore.getState();
          if (connectionError) {
            console.warn("Ignoring assessment event - connection error exists");
            return;
          }
          const { error } = useAssessmentStore.getState();
          if (deviceConflictModalOpen) {
            console.warn(
              "Ignoring assessment event - device conflict modal is open",
            );
            return;
          }
          // If there was a previous conflict error, clear it before checking again
          if (error && error.includes("close it on your other device")) {
            const { setError, clearStore } = useAssessmentStore.getState();
            setError(null);
            clearStore();
          }

          // Check for device conflict before opening assessment
          // Skip device conflict checks for admin routes
          if (!isAdminRoute()) {
            try {
              const sessionStatus = await checkSessionStatus();
              if (sessionStatus.status === "conflict") {
                const { setError, assessmentData } =
                  useAssessmentStore.getState();
                const { setIsAssessmentOpen, setDeviceConflictModalOpen } =
                  useAppStore.getState();

                // If assessment is already open on this device (old device), show device conflict modal
                if (assessmentData && assessmentData.assessment_id) {
                  setDeviceConflictModalOpen(true);
                  return;
                }

                // If trying to open new assessment (new device), show waiting indicator
                // Device conflict modal will appear automatically via useSessionStatusCheck
                setError(
                  "To take the contest, please close it on your other device first.",
                );
                setIsAssessmentOpen(true);

                // Trigger conflict check to show device-conflict-modal
                setTimeout(() => {
                  checkSessionStatus()
                    .then((status: any) => {
                      if (status.status === "conflict") {
                        setDeviceConflictModalOpen(true);
                      }
                    })
                    .catch(() => {});
                }, 500);
                return;
              }
            } catch (error) {
              console.error(
                "Error checking session status for assessment:",
                error,
              );
              // Continue with assessment if check fails
            }
          }

          if (
            data &&
            typeof data.time_limit === "number" &&
            typeof data.assessment_id === "string" &&
            Array.isArray(data.questions) &&
            data.questions.length > 0
          ) {
            const { setAssessmentData } = useAssessmentStore.getState();
            const { setIsAssessmentOpen } = useAppStore.getState();

            // Normalize the data to ensure all fields are present
            const normalizedData: AssessmentData = {
              ...data,
              title:
                typeof data.title === "string" && data.title.trim()
                  ? data.title.trim()
                  : undefined,
              total_questions_number: data.total_questions_number,
              answered_questions_number: data.answered_questions_number,
              scores_earned: data.scores_earned,
              bonus_score: data.bonus_score,
              mandatory_questions_number: data.mandatory_questions_number,
              bonus_questions_number: data.bonus_questions_number,
              mandatory_score: data.mandatory_score,
              mandatory_max_score: data.mandatory_max_score,
              bonus_question_score: data.bonus_question_score,
              bonus_question_max_score: data.bonus_question_max_score,
              user_score: data.user_score,
              gamification_bonus_score: data.gamification_bonus_score,
              contest_points: data.contest_points,
              time_left: data.time_left !== undefined ? data.time_left : null,
              total_time_spent:
                typeof data.total_time_spent === "number"
                  ? data.total_time_spent
                  : null,
              current_user_place:
                data.current_user_place !== undefined
                  ? data.current_user_place
                  : null,
              leaderboard: Array.isArray(data.leaderboard)
                ? data.leaderboard
                    .filter(
                      (item: any) =>
                        item &&
                        typeof item.user_id === "string" &&
                        typeof item.name === "string" &&
                        (typeof item.score === "number" ||
                          typeof item.user_score === "number"),
                    )
                    .map((item: any) => ({
                      ...item,
                      score:
                        typeof item.score === "number"
                          ? item.score
                          : item.user_score,
                      user_score:
                        typeof item.user_score === "number"
                          ? item.user_score
                          : item.score,
                    }))
                : undefined,
              gamification_enabled: resolveAssessmentGamificationEnabled(data),
              answer_editing_enabled: resolveAssessmentAnswerEditingEnabled(data),
              show_feedback: resolveAssessmentShowFeedback(data),
              previous_answers: Array.isArray(data.previous_answers)
                ? data.previous_answers
                : undefined,
              questions: data.questions.map((q: any, index: number) =>
                normalizeAssessmentQuestion(q, index),
              ),
            };

            setAssessmentData(normalizedData);
            setIsAssessmentOpen(true);
          } else {
            const { setError } = useAssessmentStore.getState();
            const { setIsAssessmentOpen } = useAppStore.getState();

            setError(
              "No scheduled contests are currently available for you.",
            );
            setIsAssessmentOpen(false);
            console.error("Invalid assessment data structure:", data);
          }
        } catch (error) {
          console.error("Error handling assessment data:", error);
        }
      });
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (streamingCheckIntervalRef.current) {
        clearInterval(streamingCheckIntervalRef.current);
        streamingCheckIntervalRef.current = null;
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMessages, setRefreshScheduled, setScheduledTargetMessageId]);

  // Fallback polling for missed messages when the socket is disconnected
  useEffect(() => {
    const maxConsecutiveEmptyResults = 3;
    const maxDisconnectedPollingTime = 60000;

    if (token) {
      if (!messagePollingIntervalRef.current) {
        const startMessagePolling = () => {
          if (messagePollingIntervalRef.current) {
            clearInterval(messagePollingIntervalRef.current);
          }

          messagePollingIntervalRef.current = setInterval(async () => {
            const {
              connectionError,
              deviceConflictModalOpen,
              isAssessmentOpen,
              isPreassessmentOpen,
            } = useAppStore.getState();

            if (connectionError || deviceConflictModalOpen) {
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            if (isAssessmentOpen || isPreassessmentOpen) {
              return;
            }

            const isDisconnected = !socketInstance || !socketInstance.connected;

            if (!isDisconnected) {
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            const timeSinceLastMessage =
              Date.now() - lastMessageReceivedTimeRef.current;
            const shouldPoll = isDisconnected && timeSinceLastMessage > 15000;

            if (!shouldPoll) {
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            if (isDisconnected && !disconnectedPollingStartTimeRef.current) {
              disconnectedPollingStartTimeRef.current = Date.now();
            }

            // Permanent disconnect (e.g. device conflict) — skip polling this tick
            if (
              isDisconnected &&
              socketInstance &&
              socketInstance.io &&
              socketInstance.io.opts &&
              socketInstance.io.opts.reconnection === false
            ) {
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            if (
              isDisconnected &&
              disconnectedPollingStartTimeRef.current &&
              Date.now() - disconnectedPollingStartTimeRef.current >
                maxDisconnectedPollingTime
            ) {
              if (messagePollingIntervalRef.current) {
                clearInterval(messagePollingIntervalRef.current);
                messagePollingIntervalRef.current = null;
              }
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            if (
              consecutiveEmptyResultsRef.current >= maxConsecutiveEmptyResults
            ) {
              if (messagePollingIntervalRef.current) {
                clearInterval(messagePollingIntervalRef.current);
                messagePollingIntervalRef.current = null;
              }
              consecutiveEmptyResultsRef.current = 0;
              disconnectedPollingStartTimeRef.current = null;
              return;
            }

            try {
              if (!shouldSyncMissedMessages()) {
                consecutiveEmptyResultsRef.current += 1;
                return;
              }

              const beforeCount = useChatStore.getState().messages.length;
              const didRun = await runMissedMessageSync(
                () => syncMissedMessagesRef.current?.() ?? Promise.resolve(),
              );

              if (!didRun) {
                consecutiveEmptyResultsRef.current += 1;
                return;
              }

              const foundNewMessages =
                useChatStore.getState().messages.length > beforeCount;

              if (foundNewMessages) {
                lastMessageReceivedTimeRef.current = Date.now();
                consecutiveEmptyResultsRef.current = 0;
                disconnectedPollingStartTimeRef.current = null;
              } else {
                consecutiveEmptyResultsRef.current += 1;
              }
            } catch (error) {
              console.error("Error in message polling:", error);
              consecutiveEmptyResultsRef.current += 1;
            }
          }, 5000);
        };

        startMessagePolling();
      }
    } else {
      if (messagePollingIntervalRef.current) {
        clearInterval(messagePollingIntervalRef.current);
        messagePollingIntervalRef.current = null;
      }
      consecutiveEmptyResultsRef.current = 0;
      disconnectedPollingStartTimeRef.current = null;
    }

    return () => {
      // Cleanup: always stop polling when effect unmounts or dependencies change
      if (messagePollingIntervalRef.current) {
        clearInterval(messagePollingIntervalRef.current);
        messagePollingIntervalRef.current = null;
      }
      consecutiveEmptyResultsRef.current = 0;
      disconnectedPollingStartTimeRef.current = null;
    };
  }, [setMessages, token]);

  useEffect(() => {
    if (!socketInstance) return;

    const { connectionError, deviceConflictModalOpen } = useAppStore.getState();
    if (connectionError) {
      if (socketInstance.connected) {
        socketInstance.disconnect();
      }
      if (socketInstance.io && socketInstance.io.opts) {
        socketInstance.io.opts.reconnection = false;
      }
      return;
    }

    // Activation owns connect/disconnect while the conflict modal stays open.
    if (isSessionActivationInFlight()) {
      return;
    }

    // Don't connect if device conflict modal is open
    if (deviceConflictModalOpen) {
      if (socketInstance.connected) {
        socketInstance.disconnect();
      }
      if (socketInstance.io && socketInstance.io.opts) {
        socketInstance.io.opts.reconnection = false;
      }
      return;
    }

    // Don't connect while verifying a server-side kick / device conflict
    if (serverDisconnectVerificationInFlight) {
      if (socketInstance.connected) {
        socketInstance.disconnect();
      }
      if (socketInstance.io && socketInstance.io.opts) {
        socketInstance.io.opts.reconnection = false;
      }
      return;
    }

    if (!token) {
      previousTokenForConnectRef.current = null;
      cancelPendingSocketReconnects();
      if (socketInstance.connected) {
        socketInstance.disconnect();
      }
      if (socketInstance.io && socketInstance.io.opts) {
        socketInstance.io.opts.reconnection = false;
      }
      return;
    }

    const previousToken = previousTokenForConnectRef.current;
    const tokenChanged = previousToken !== null && previousToken !== token;
    const focusChanged = previousFocusRef.current !== focus;
    const domainChanged = previousDomainRef.current !== domain;

    previousTokenForConnectRef.current = token;
    previousFocusRef.current = focus;
    previousDomainRef.current = domain;

    if (!domain || !focus) {
      return;
    }

    applySocketAuth(socketInstance, token, focus, domain);

    if (!socketInstance.connected) {
      if (socketInstance.io && socketInstance.io.opts) {
        socketInstance.io.opts.reconnection = true;
      }
      socketInstance.connect();
    } else if (tokenChanged || focusChanged || domainChanged) {
      socketInstance.disconnect();
      scheduleSocketReconnect(() => {
        if (
          token &&
          domain &&
          focus &&
          socketInstance &&
          !socketInstance.connected
        ) {
          applySocketAuth(socketInstance, token, focus, domain);
          if (socketInstance.io && socketInstance.io.opts) {
            socketInstance.io.opts.reconnection = true;
          }
          socketInstance.connect();
        }
      }, 100);
    } else {
      applySocketAuth(socketInstance, token, focus, domain);
    }
  }, [token, focus, domain]);

  useEffect(() => {
    if (!socketInstance || !token) return;

    const reconnectIfNeeded = () => {
      const { connectionError, deviceConflictModalOpen } =
        useAppStore.getState();

      if (
        connectionError ||
        deviceConflictModalOpen ||
        isSessionActivationInFlight()
      ) {
        return;
      }

      if (serverDisconnectVerificationInFlight) {
        return;
      }

      if (!socketInstance) {
        return;
      }

      const currentToken = tokenRef.current;
      const currentFocus = focusRef.current;
      const currentDomain = domainRef.current;

      if (!currentToken || !currentFocus || !currentDomain) {
        return;
      }

      applySocketAuth(socketInstance, currentToken, currentFocus, currentDomain);

      if (socketInstance && !socketInstance.connected && tokenRef.current) {
        applySocketAuth(
          socketInstance,
          tokenRef.current,
          focusRef.current,
          domainRef.current,
        );
        if (socketInstance.io && socketInstance.io.opts) {
          socketInstance.io.opts.reconnection = true;
        }
        socketInstance.connect();
      } else if (socketInstance && socketInstance.connected) {
        if (tokenRef.current && focusRef.current && domainRef.current) {
          applySocketAuth(
            socketInstance,
            tokenRef.current,
            focusRef.current,
            domainRef.current,
          );
        }
      }
    };

    const resumeConnectionIfNeeded = () => {
      const { connectionError, deviceConflictModalOpen } =
        useAppStore.getState();

      if (
        connectionError ||
        deviceConflictModalOpen ||
        isSessionActivationInFlight() ||
        !tokenRef.current
      ) {
        return;
      }

      if (!socketInstance) {
        return;
      }

      if (!socketInstance.connected) {
        reconnectIfNeeded();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        documentHiddenAt = Date.now();
        return;
      }

      if (document.visibilityState !== "visible") {
        return;
      }

      const hiddenDuration = documentHiddenAt
        ? Date.now() - documentHiddenAt
        : 0;
      documentHiddenAt = null;

      resumeConnectionIfNeeded();
      void verifySocketRegistration();

      scheduleMissedMessageSyncAfterTabVisible(
        hiddenDuration,
        () => syncMissedMessagesRef.current?.() ?? Promise.resolve(),
      );
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (document.visibilityState === "visible") {
      resumeConnectionIfNeeded();
    }

    const connectionCheckInterval = setInterval(() => {
      if (document.visibilityState === "visible" && token) {
        resumeConnectionIfNeeded();
      }
    }, 5000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(connectionCheckInterval);
    };
  }, [token]);

  const sendMessage = (message: Message) => {
    const { connectionError, deviceConflictModalOpen } = useAppStore.getState();
    if (connectionError) {
      return;
    }
    if (deviceConflictModalOpen) {
      console.warn("Cannot send message - device conflict modal is open");
      return;
    }

    if (!socketInstance) {
      console.warn("Socket instance not available");
      return;
    }

    const messageText =
      typeof message.text === "string" ? message.text.trim().toLowerCase() : "";
    if (messageText.startsWith("/clear_assessments")) {
      const { clearStore } = useAssessmentStore.getState();
      const { setIsAssessmentOpen } = useAppStore.getState();
      clearStore();
      setIsAssessmentOpen(false);
    }

    if (isSessionActivationInFlight()) {
      enqueuePendingMessage(message);
      return;
    }

    if (socketInstance.connected) {
      socketInstance.emit("message", message);
      return;
    }

    enqueuePendingMessage(message);

    const currentToken = tokenRef.current;
    if (!currentToken || !focusRef.current || !domainRef.current) {
      console.warn("No token available for sending message");
      return;
    }

    applySocketAuth(
      socketInstance,
      currentToken,
      focusRef.current,
      domainRef.current,
    );

    if (socketInstance.io?.opts) {
      socketInstance.io.opts.reconnection = true;
    }

    socketInstance.connect();
  };

  return { sendMessage };
};

